<template>
  <div style="position: relative" class="map-card-imgbanner">
    <img
      v-if="!cssClassImageLoaded && !isNeighborhoodImage"
      :src="imgUrl"
      :alt="alt"
      @error="onImageError"
      :class="[
        cssClassImageLoaded ? 'lazyimg-fade-out' : '',
        'lazyimg op-1',
        { fitImage: cssDefaultImage },
      ]"
    />

    <h1
      v-if="isNeighborhoodImage && neighborhoodImageError"
      class="imageErrorMessage"
    >
      {{ imageError }}
    </h1>

    <img
      v-if="!neighborhoodImageError"
      :src="imgUrl"
      :alt="alt"
      @load="handleImageLoad"
      @error="onImageError"
      :class="[
        cssClassImageLoaded ? 'lazyimg-fade-in' : '',
        'lazyimg op-0',
        { fitImage: cssDefaultImage },
      ]"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, toRefs } from "vue";
import { removeImageScaler } from "@/core";

interface Props {
  src: string;
  alt: string;
  isNeighborhoodImage: boolean;
  showDefaultImage?: boolean;
}

const props = defineProps<Props>();
const { src, alt, isNeighborhoodImage, showDefaultImage } = toRefs(props);

const imageError = ref("");
const neighborhoodImageError = ref(false);
const cssClassImageLoaded = ref(false);
const cssDefaultImage = ref(showDefaultImage?.value ?? false);
const imgUrl = ref(src.value);
const lastFailedUrl = ref("");

watch(src, () => {
  imageError.value = "";
  neighborhoodImageError.value = false;
  cssClassImageLoaded.value = false;
  cssDefaultImage.value = showDefaultImage?.value ?? false;
  lastFailedUrl.value = "";
  imgUrl.value = src.value;
});

onMounted(() => {
  imgUrl.value = src.value;
  cssDefaultImage.value = showDefaultImage?.value ?? false;
});

const handleImageLoad = () => {
  cssClassImageLoaded.value = true;
};

const onImageError = () => {
  // Both <img> elements below share `imgUrl`, so one broken URL fires this
  // handler twice. Without this guard the second event would skip the unscaled
  // retry and jump straight to the placeholder.
  if (lastFailedUrl.value === imgUrl.value) {
    return;
  }
  lastFailedUrl.value = imgUrl.value;

  if (isNeighborhoodImage.value) {
    neighborhoodImageError.value = true;
    imageError.value = alt.value.charAt(0);
    return;
  }

  const unscaledUrl = removeImageScaler(src.value);
  if (!cssDefaultImage.value && unscaledUrl && unscaledUrl !== imgUrl.value) {
    cssDefaultImage.value = true;
    imgUrl.value = unscaledUrl;
    return;
  }

  imgUrl.value = require("@/assets/images/home-not-available.png");
};
</script>

<style lang="scss" scoped>
.lazyimg {
  transition: opacity 2s ease;
}

.lazyimg-fade-in {
  opacity: 1 !important;
  z-index: 2 !important;
}

.lazyimg-fade-out {
  opacity: 0 !important;
  z-index: 0 !important;
}

.op-0 {
  opacity: 0;
  position: relative;
}

.op-1 {
  position: absolute;
  opacity: 1;
  filter: blur(5px);
  overflow: hidden;
  background-size: 100% 100%;
}

.lazyimg-load {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
}

.imageErrorMessage {
  position: absolute;
  background-color: silver;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  text-align: center;
  padding: 10px;
}

.fitImage {
  object-fit: fill;
}
</style>
